import { CalendarDayIcon, emptyFragment } from '@execonline-inc/execonline-ui';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { segmentRoute } from '../../ClientRoutes';
import { StartDate, UpcomingProgram, upcomingCurrentSegment } from '../../ProgramStore/Types';
import { Link } from '../../Resource/Types';
import { L, NotTranslated, T } from '../../Translations';
import {
  ProgramCard,
  ProgramCardBody,
  ProgramCardFooter,
  ProgramCardHeader,
  ProgramCardPanel,
  ProgramCardTitle,
} from '../Dashboard/ProgramCard';
import { whenProgramProductDetails } from '../Dashboard/ProgramCard/Functions';
import LiveSessions from '../Dashboard/ProgramCard/LiveSessions';

interface Props {
  programDetail: UpcomingProgram;
  links: ReadonlyArray<Link>;
}

const ProgramStart: React.FC<{ startDate: StartDate }> = ({ startDate }) => {
  switch (startDate.kind) {
    case 'starts-on':
      return (
        <p className="m-0 text-lg font-semibold text-gray-900">
          <T
            kind="Program starts <date/>"
            date={<L localizeable={startDate.date} format="long-month-day-year" />}
          />
        </p>
      );
    case 'starts-anytime':
      return emptyFragment();
  }
};

const ProgramCardContent: React.FC<Props> = observer(({ programDetail, links }) => (
  <ProgramCard data-test-upcoming-program={true}>
    <ProgramCardHeader links={links} programInstanceKind={programDetail.programInstanceKind} />
    <ProgramCardTitle title={programDetail.title} />
    <ProgramCardBody>
      <ProgramCardPanel>
        <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-500">
          <CalendarDayIcon size="xl" color="inherit" />
        </div>
        <div className="min-w-0">
          <ProgramStart startDate={programDetail.startDate} />
          <p className="m-0 mt-1 text-gray-600" data-test-program-message={programDetail.message}>
            <NotTranslated text={programDetail.message} />
          </p>
        </div>
      </ProgramCardPanel>
    </ProgramCardBody>
    <ProgramCardFooter>
      {whenProgramProductDetails(programDetail)
        .map((productDetails) => <LiveSessions productDetails={productDetails} />)
        .getOrElse(emptyFragment)}
    </ProgramCardFooter>
  </ProgramCard>
));

// The whole card opens the course at the learner's current segment. The program has
// not started, so the segment page shows when it opens, with the course tools.
const UpcomingProgramOverview: React.FC<Props> = (props) =>
  upcomingCurrentSegment(props.programDetail)
    .map((segment) => (
      <RouterLink
        to={segmentRoute(segment)}
        className="block rounded text-inherit no-underline transition-shadow hover:shadow-md"
        data-intercom-target="dashboard-upcoming-program-link"
        data-test-upcoming-program-link={segment.id}
      >
        <ProgramCardContent {...props} />
      </RouterLink>
    ))
    .getOrElse(() => <ProgramCardContent {...props} />);

export default observer(UpcomingProgramOverview);
